/**
 * tRPC Client for Console App
 *
 * Configures the tRPC client for Vite SPA.
 * Provides typed hooks for React components (useQuery, useMutation, etc.).
 *
 * @module @console/lib/trpc
 */

import { createTRPCReact } from "@trpc/react-query";
import { httpBatchLink } from "@trpc/client";
import superjson from "superjson";
import type { AppRouter } from "@platform/trpc";

/**
 * tRPC React client with full type inference
 *
 * @example
 * ```tsx
 * import { trpc } from '@/lib/trpc';
 *
 * function Dashboard() {
 *   const { data, isLoading } = trpc.dashboard.getOverview.useQuery();
 *   if (isLoading) return <div>Loading...</div>;
 *   return <div>MRR: ${data?.mrr}</div>;
 * }
 * ```
 */
export const trpc = createTRPCReact<AppRouter>();

/**
 * Creates the tRPC client configuration
 *
 * Used by TRPCProvider to initialize the client.
 * Configures HTTP batch link, superjson transformer, and credentials.
 */
export function createTRPCClientConfig() {
  return {
    links: [
      httpBatchLink({
        url: `${import.meta.env.VITE_API_URL || ""}/api/trpc`,
        transformer: superjson,
        fetch(url, options) {
          return fetch(url, {
            ...options,
            credentials: "include",
          });
        },
      }),
    ],
  };
}

/**
 * Type exports for use in components
 *
 * @example
 * ```tsx
 * import type { RouterOutputs } from '@/lib/trpc';
 * type DashboardOverview = RouterOutputs['dashboard']['getOverview'];
 * ```
 */
export type { AppRouter };
